import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Logo } from '../ui'
import {
  USER_REGISTER_MUTATION,
  type UserRegisterResponse,
  type UserRegisterVariables,
} from '../../graphql/mutations'
import { useAuth } from '../../contexts/AuthContext/AuthContext'
import { useNavigate } from 'react-router-dom'
import { ROOT_PATH } from '../../config/rootPath'

export function SignupPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    passwordConfirmation: ''
  })
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [serverError, setServerError] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const [registerUser, { loading: mutationLoading }] = useMutation<UserRegisterResponse, UserRegisterVariables>(
    USER_REGISTER_MUTATION,
    {
      onCompleted: (data) => {
        if (data.userRegister && data.userRegister.token && data.userRegister.user) {
          // Handle successful registration
          localStorage.setItem('authToken', data.userRegister.token)
          localStorage.setItem('user', JSON.stringify(data.userRegister.user))
          window.dispatchEvent(new Event('storage'))
          // Navigate to dashboard
          window.location.href = '/dashboard'
        } else if (data.userRegister && data.userRegister.errors && data.userRegister.errors.length > 0) {
          setServerError(data.userRegister.errors.join(', '))
        }
      },
      onError: (_error) => {
        setServerError('Unable to create account. Please try again later.')
      },
    }
  )
  
  const { login: _login } = useAuth()
  const _navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setFieldErrors({})
    setServerError('')
    setIsSubmitting(true)

    const errors: Record<string, string> = {}

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required'
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required'
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format'
    }
    if (!formData.password) {
      errors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters'
    }
    if (!formData.passwordConfirmation) {
      errors.passwordConfirmation = 'Confirm password is required'
    } else if (formData.password !== formData.passwordConfirmation) {
      errors.passwordConfirmation = 'Passwords must match'
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setIsSubmitting(false)
      return
    }

    try {
      await registerUser({
        variables: {
          input: {
            email: formData.email,
            password: formData.password,
            passwordConfirmation: formData.passwordConfirmation,
            firstName: formData.firstName,
            lastName: formData.lastName
          }
        }
      })
    } catch (error) {
      setServerError('An unexpected error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
 name, value 
} = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

    return (
    <>
      <div className="relative flex min-h-screen shrink-0 justify-center md:px-12 lg:px-0">
        <div className="relative z-10 flex flex-1 flex-col bg-white px-4 py-10 shadow-2xl sm:justify-center md:flex-none md:px-28">
          <main className="mx-auto w-full max-w-md sm:px-4 md:w-96 md:max-w-sm md:px-0">
            <div className="flex">
              <a aria-label="Home" href={ROOT_PATH}>
                <Logo variant="image" showText={true} size="md" />
              </a>
            </div>
            <h2 className="mt-20 text-lg font-semibold text-gray-900">Get started for free</h2>
            <p className="mt-2 text-sm text-gray-700">
              Already registered?{' '}
              <a className="font-medium text-blue-600 hover:underline" href="/login">
                Sign in
              </a>{' '}
              to your account.
            </p>
            
            {serverError && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mt-4">
                <p className="text-sm font-medium">{serverError}</p>
              </div>
            )}
            
            <form className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="firstName" className="mb-3 block text-sm font-medium text-gray-700">
                  First name
                </label>
                <input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="block w-full appearance-none rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-hidden focus:ring-blue-500 sm:text-sm"
                  name="firstName"
                />
                {fieldErrors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.firstName}</p>
                )}
              </div>
              <div>
                <label htmlFor="lastName" className="mb-3 block text-sm font-medium text-gray-700">
                  Last name
                </label>
                <input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="block w-full appearance-none rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-hidden focus:ring-blue-500 sm:text-sm"
                  name="lastName"
                />
                {fieldErrors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.lastName}</p>
                )}
              </div>
              <div className="col-span-full">
                <label htmlFor="email" className="mb-3 block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full appearance-none rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-hidden focus:ring-blue-500 sm:text-sm"
                  name="email"
                />
                {fieldErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
                )}
              </div>
              <div className="col-span-full">
                <label htmlFor="password" className="mb-3 block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full appearance-none rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-hidden focus:ring-blue-500 sm:text-sm"
                  name="password"
                />
                {fieldErrors.password && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
                )}
              </div>
              <div className="col-span-full">
                <label htmlFor="passwordConfirmation" className="mb-3 block text-sm font-medium text-gray-700">
                  Confirm password
                </label>
                <input
                  id="passwordConfirmation"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.passwordConfirmation}
                  onChange={handleChange}
                  className="block w-full appearance-none rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-hidden focus:ring-blue-500 sm:text-sm"
                  name="passwordConfirmation"
                />
                {fieldErrors.passwordConfirmation && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.passwordConfirmation}</p>
                )}
              </div>
              <div className="col-span-full">
                <button
                  className="group inline-flex items-center justify-center rounded-full py-2 px-4 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 bg-blue-600 text-white hover:text-slate-100 hover:bg-blue-500 active:bg-blue-800 active:text-blue-100 focus-visible:outline-blue-600 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  type="submit"
                  disabled={isSubmitting || mutationLoading}
                >
                  <span>
                    {isSubmitting || mutationLoading ? 'Signing up...' : 'Sign up'} <span aria-hidden="true">→</span>
                  </span>
                </button>
              </div>
            </form>
          </main>
        </div>
        <div className="hidden sm:contents lg:relative lg:block lg:flex-1">
          <img
            alt=""
            loading="lazy"
            width="1664"
            height="1866"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ color: 'transparent' }}
            src="/background-auth.jpg"
          />
        </div>
      </div>
    </>
  )
} 